/**
 * Resumo compacto das partes de um processo — fonte única de formatação.
 *
 * Produz rótulos como "Fulano x Beltrano e outros (2)" para exibir junto ao
 * número do processo na lista de processos, lista/sheet de comunicações, autos
 * digitais e dashboard.
 *
 * Aceita qualquer fonte que tenha `polo` + `nome`:
 *  - partes dos autos (`Parte`, polo "ATIVO"/"PASSIVO"/"OUTROS_PARTICIPANTES");
 *  - destinatários do DJEN (`destinatarios_json`, polo possivelmente "A"/"P");
 *  - linhas internas `parte` (polo derivado de tipo_parte "AUTOR"/"REU"/...).
 *
 * Equivalente Angular/Java: um @Pipe puro / método estático de formatação —
 * sem estado, sem efeitos, testável isoladamente.
 */

/** Forma mínima consumida pelo resumo. */
export interface ParteResumivel {
  polo?: string | null;
  nome?: string | null;
}

type PoloNormalizado = 'ATIVO' | 'PASSIVO' | 'OUTROS';

/** Resumo de um polo: primeira parte + nº de partes adicionais DO MESMO polo. */
export interface PoloResumo {
  primeiro: string;
  /** Quantas outras partes existem no mesmo polo além da primeira. */
  outros: number;
}

export interface PartesResumo {
  ativo: PoloResumo | null;
  passivo: PoloResumo | null;
  /**
   * Há nome presente nos DOIS polos — típico de recurso cruzado (réu apela, MP
   * apela) e de ação penal com vários réus. Nesse caso "A x B" é PROPOSIÇÃO
   * FALSA: apresenta como adversários quem está do mesmo lado. Quando `true`, o
   * `label` cai para a forma neutra "N partes".
   */
  polosCruzados: boolean;
  /** Rótulo pronto para exibição: "Fulano x Beltrano e outros (2)". */
  label: string;
}

/**
 * Conserta a caixa de um nome que o DJEN entrega "meio maiúsculo".
 *
 * A API do DJEN põe em maiúscula só as letras ASCII: "MINISTéRIO PúBLICO",
 * "JOSé", "ADMINISTRAçãO". Nome sem nenhuma minúscula ASCII mas com letra
 * acentuada minúscula é esse defeito, e o certo é a maiúscula inteira. Nome em
 * caixa mista ("Fulano de Tal") passa intacto.
 *
 * Só a exibição muda; o dado gravado continua como a fonte o entregou.
 */
export function fixNameCase(nome: string): string {
  if (/[a-z]/.test(nome)) return nome;
  if (!/[à-öø-ÿ]/.test(nome)) return nome;
  return nome.toLocaleUpperCase('pt-BR');
}

/**
 * Chave de identidade de um nome: sem acento não faz falta aqui (a fonte é a
 * mesma em ambas as ocorrências), mas caixa e espaço em excesso sim — a PDPJ
 * repete a mesma parte com grafias que só diferem nisso.
 */
function chaveNome(nome: string): string {
  return nome.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Remove repetições preservando a ordem de aparição. */
function deduplicar(nomes: string[]): string[] {
  const vistos = new Set<string>();
  const unicos: string[] = [];
  for (const nome of nomes) {
    const chave = chaveNome(nome);
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    unicos.push(nome);
  }
  return unicos;
}

/**
 * Normaliza o polo de qualquer fonte para ATIVO / PASSIVO / OUTROS. Tolerante
 * às variações conhecidas (autos, DJEN, tipo_parte interno). Polo desconhecido
 * cai em OUTROS — nunca é descartado silenciosamente.
 */
function normalizarPolo(polo: string | null | undefined): PoloNormalizado {
  const p = (polo ?? '').trim().toUpperCase();
  if (!p) return 'OUTROS';
  if (p === 'A' || p.startsWith('ATIV') || p === 'AUTOR') return 'ATIVO';
  if (p === 'P' || p.startsWith('PASS') || p === 'REU' || p === 'RÉU') return 'PASSIVO';
  return 'OUTROS';
}

function segmento(polo: PoloResumo | null): string | null {
  if (!polo) return null;
  return polo.outros > 0 ? `${polo.primeiro} e outros (${polo.outros})` : polo.primeiro;
}

/**
 * Monta o resumo das partes. Regras (cobrindo os casos de borda):
 *  - sem partes / sem nenhum nome válido → `null` (a UI não renderiza nada);
 *  - ativo + passivo → "Fulano x Beltrano" (+ "e outros (N)" por polo);
 *  - só um polo (comum em intimações) → mostra só o que houver;
 *  - só terceiros/outros participantes → mostra a primeira parte;
 *  - "e outros (N)" conta as partes adicionais DO MESMO polo exibido, JÁ SEM
 *    repetições — a PDPJ lista a mesma parte mais de uma vez (uma por advogado
 *    ou por relação processual) e a contagem crua inflava o número exibido;
 *  - terceiros não entram no headline ativo x passivo quando há ativo ou passivo;
 *  - **nome nos dois polos** (recurso cruzado, ação penal multirréu) → o par
 *    adversarial é falso e o rótulo vira "N partes". Ver `polosCruzados`.
 */
export function resumirPartes(partes: ParteResumivel[] | null | undefined): PartesResumo | null {
  // Tolerante a JSON cru (destinatarios_json pode não ser um array).
  if (!Array.isArray(partes) || partes.length === 0) return null;

  const grupos: Record<PoloNormalizado, string[]> = { ATIVO: [], PASSIVO: [], OUTROS: [] };
  for (const parte of partes) {
    const nome = fixNameCase((parte?.nome ?? '').trim());
    if (!nome) continue;
    grupos[normalizarPolo(parte?.polo)].push(nome);
  }

  const nomesAtivo = deduplicar(grupos.ATIVO);
  const nomesPassivo = deduplicar(grupos.PASSIVO);
  const nomesOutros = deduplicar(grupos.OUTROS);

  const toResumo = (nomes: string[]): PoloResumo | null =>
    nomes.length === 0 ? null : { primeiro: nomes[0], outros: nomes.length - 1 };

  const ativo = toResumo(nomesAtivo);
  const passivo = toResumo(nomesPassivo);
  const outros = toResumo(nomesOutros);

  // Interseção entre os polos: mesma pessoa recorrente E recorrida.
  const chavesPassivo = new Set(nomesPassivo.map(chaveNome));
  const polosCruzados = nomesAtivo.some((nome) => chavesPassivo.has(chaveNome(nome)));

  if (polosCruzados) {
    const total = deduplicar([...nomesAtivo, ...nomesPassivo, ...nomesOutros]).length;
    return {
      ativo,
      passivo,
      polosCruzados,
      label: total === 1 ? '1 parte' : `${total} partes`,
    };
  }

  const segAtivo = segmento(ativo);
  const segPassivo = segmento(passivo);

  let label: string;
  if (segAtivo && segPassivo) {
    label = `${segAtivo} x ${segPassivo}`;
  } else if (segAtivo) {
    label = segAtivo;
  } else if (segPassivo) {
    label = segPassivo;
  } else if (outros) {
    label = segmento(outros)!;
  } else {
    return null;
  }

  return { ativo, passivo, polosCruzados, label };
}

/** Atalho para quando só o rótulo importa. Retorna `null` se não houver partes. */
export function formatPartesResumo(partes: ParteResumivel[] | null | undefined): string | null {
  return resumirPartes(partes)?.label ?? null;
}
